import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { BiSolidError } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { IoCloseSharp, IoSearchSharp } from "react-icons/io5";
import { MdOutlineEdit } from "react-icons/md";
import { RiDeleteBin6Fill } from "react-icons/ri";
import "react-loading-skeleton/dist/skeleton.css";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import ActionMenu from "../../../component/ActionMenu";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import i18n from "../../../i8next";
import {
  useDeleteMaintainanceMutation,
  useGetAllMaintainanceQuery,
} from "../../../store/api/vendor/MaintainanceSlice";
import {
  capitalizeFirstLetter,
  formatDateToLocal,
} from "../../../utils/dateAndTimeConversion";

const initialPagination = {
  limit: 10,
  page: 1,
  total: 0,
  totalPages: 0,
};

const initialActions = {
  search: "",
  maintenance_type: "",
};

const MaintenanceInspection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search.trim(), 300);
  const [action, setAction] = useState(initialActions);
  const [maintainance, setMaintainance] = useState([]);
  const [id, setId] = useState("");
  const { role } = useParams();
  const [pagination, setPagination] = useState(initialPagination);

  // Get All maintenance
  const { data, isSuccess, isLoading, isError, error, isFetching, refetch } =
    useGetAllMaintainanceQuery({
      page: pagination.page,
      limit: pagination.limit,
      ...action,
    });

  // Delete Maintainance
  const [deleteMaintainance, { isLoading: isDeleteLoading }] =
    useDeleteMaintainanceMutation();

  // Effect to update data and pagination
  useEffect(() => {
    if (isSuccess && data?.data) {
      setMaintainance(data?.data?.maintenanceRecords);
      setPagination(data.data.pagination);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (debouncedSearch) {
      setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: search.trim() }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: "" }));
    }
  }, [search]);

  // Model Show Function
  const handleShow2 = (id) => {
    setShow(true);
    setId(id);
  };

  // filters chnages
  const handleChange = (e) => {
    const { name, value } = e.target;
    setAction((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ limit: 10, page: 1 }));
  };

  // Pagination Change
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  // Delete Route Shedule
  const handleDelete = async () => {
    toast.dismiss();
    try {
      const response = await deleteMaintainance(id).unwrap();
      if (response.statusCode === 200) {
        toast.success(response.message);
        setShow(false);
        setId("");
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  const handleResetFilters = () => {
    setAction(initialActions);
    setPagination((prev) => ({
      ...prev,
      limit: 10,
      page: 1,
    }));
    setSearch("");
  };

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control custom-field-one"
              placeholder={`${t("search")}..`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>
          <div className="d-flex flex-grow-1">
            <select
              className="form-select"
              name="maintenance_type"
              value={action.maintenance_type}
              onChange={handleChange}
            >
              <option value="">
                {t("select")} {t("maintenance")} {t("type")}
              </option>
              <option value="routine">{t("routine")}</option>
              <option value="repair">{t("repair")}</option>
              <option value="inspection">{t("inspection")}</option>
              <option value="emergency">{t("emergency")}</option>
            </select>
          </div>

          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-primary addDriver"
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            <NavLink
              to="add"
              className="page-add-btn d-flex align-items-center justify-content-center gap-2"
            >
              <FaPlus /> {t("add")} {t("maintenance")} & {t("inspection")}
            </NavLink>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>{`${t("Sr")}. ${t("no")}.`}</th>
              <th>{t("vehicles")}</th>
              <th>{t("type")}</th>
              <th>{t("date")}</th>
              <th>{t("Cost")}</th>
              <th>{`${t("km")}/${t("miles")}`}</th>
              <th>{`${t("expected")} ${t("date")}`}</th>
              <th>{t("description")}</th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading length={9} />
            ) : maintainance?.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center">
                  {t("no")} {t("maintenance")} {t("records")} {t("found")}.
                </td>
              </tr>
            ) : (
              maintainance.map((item, i) => {
                return (
                  <tr key={item.id}>
                    <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                    <td>{item?.vehicle?.vehicle_type}</td>
                    <td>{capitalizeFirstLetter(item.maintenance_type)}</td>
                    <td>{formatDateToLocal(item?.maintenance_date)}</td>
                    <td>${item.cost}</td>
                    <td>{item.km_miles}</td>
                    <td>{formatDateToLocal(item?.expected_date)}</td>
                    <td>{item.description}</td>
                    <td>
                      <ActionMenu
                        actions={[
                          {
                            label: t("edit"),
                            icon: <MdOutlineEdit size={16} />,
                            onClick: () =>
                              navigate(
                                `/maintenance-and-inspection/edit/${item.id}`,
                              ),
                          },
                          {
                            label: t("btns.delete"),
                            icon: <RiDeleteBin6Fill size={15} />,
                            onClick: () => handleShow2(item.id),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />
      {/* Delete Confirmation Modal */}
      <Modal show={show}>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 errorIcon">
              <BiSolidError />
            </div>
            <div className="text-center mb-4 deleteHd">
              {t("confirmDelete")}
            </div>
            <div className="text-center">
              <Button className="smallBtn cancel me-3" onClick={handleClose}>
                {t("btns.cancel")}
              </Button>
              <Button
                className="smallBtn auto"
                onClick={handleDelete}
                disabled={isDeleteLoading}
              >
                {isDeleteLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("btns.delete")
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <style jsx>
        {`
          .smallSelect {
            width: 220px;
          }
        `}
      </style>
    </>
  );
};

export default MaintenanceInspection;
